import React, { KeyboardEvent, useRef, useCallback } from "react";

import Grid from "@mui/material/Grid";

import { CodeDigitInput } from "./CodeDigitInput";

type SixDigitsCodeProps = {
  code: string;
  setCode: (code: string) => void;
  onEnter?: () => void;
  autoFocus?: boolean;
};

export const SixDigitsCode: React.FC<SixDigitsCodeProps> = ({ code, setCode, onEnter, autoFocus = true }) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const digits = code.split("").concat(Array(6).fill("")).slice(0, 6);

  const handleChange = useCallback(
    (index: number, value: string) => {
      if (!/^[0-9]?$/.test(value)) {
        return;
      } // Only allow single digit

      const newDigits = [...digits];
      newDigits[index] = value;
      const newCode = newDigits.join("");
      setCode(newCode);

      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [digits, setCode]
  );

  const handleKeyDown = useCallback(
    (index: number, event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Backspace") {
        if (digits[index] === "" && index > 0) {
          const newDigits = [...digits];
          newDigits[index - 1] = "";
          const newCode = newDigits.join("");
          setCode(newCode);
          inputRefs.current[index - 1]?.focus();
          event.preventDefault();
        }
      } else if (event.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
        event.preventDefault();
      } else if (event.key === "ArrowRight" && index < 5) {
        inputRefs.current[index + 1]?.focus();
        event.preventDefault();
      } else if (event.key === "Enter" && digits.every((d) => d !== "")) {
        onEnter?.();
      }
    },
    [digits, setCode, onEnter]
  );

  return (
    <Grid container justifyContent="center" spacing={1}>
      {[...Array(6)].map((_: unknown, index) => (
        <Grid item key={index}>
          <CodeDigitInput
            value={digits[index]}
            onChange={(value) => handleChange(index, value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            inputRef={(el) => {
              inputRefs.current[index] = el;
            }}
            autoFocus={autoFocus && index === 0}
            index={index}
          />
        </Grid>
      ))}
    </Grid>
  );
};
